// SearchBar — Type-ahead unified search with voice STT.
// Uses Web Speech API for voice (best-effort, Chrome/Edge desktop + Android Chrome).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search, Mic, Loader2, X, BookOpen, PlaySquare, Gamepad2, Sparkles } from 'lucide-react';
import { api } from '@/lib/api';

const KIND_ICON = {
    course: BookOpen,
    video: PlaySquare,
    game: Gamepad2,
    story: Sparkles,
};

export default function SearchBar() {
    const [q, setQ] = useState('');
    const [open, setOpen] = useState(false);
    const [results, setResults] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [listening, setListening] = useState(false);
    const recogRef = useRef(null);
    const inputRef = useRef(null);
    const debounceRef = useRef(null);
    const navigate = useNavigate();

    const search = useCallback(async (text) => {
        if (!text.trim()) {
            setResults([]);
            try {
                const r = await api.get(`/search/suggest?q=`);
                setSuggestions(r.data?.suggestions || []);
            } catch { setSuggestions([]); }
            return;
        }
        setLoading(true);
        try {
            const [s, sg] = await Promise.all([
                api.get(`/search?q=${encodeURIComponent(text)}&limit=12`),
                api.get(`/search/suggest?q=${encodeURIComponent(text)}`),
            ]);
            setResults(s.data?.results || []);
            setSuggestions(sg.data?.suggestions || []);
        } catch {
            setResults([]); setSuggestions([]);
        } finally { setLoading(false); }
    }, []);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => { search(q); }, 180);
        return () => clearTimeout(debounceRef.current);
    }, [q, search]);

    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault(); setOpen(true);
                setTimeout(() => inputRef.current?.focus(), 60);
            } else if (e.key === 'Escape' && open) {
                setOpen(false);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open]);

    const startVoice = () => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) {
            alert('Voice search is not supported in this browser. Try Chrome on desktop or Android.');
            return;
        }
        const r = new SR();
        r.lang = 'en-IN';
        r.continuous = false;
        r.interimResults = true;
        r.maxAlternatives = 1;
        r.onresult = (ev) => {
            const txt = Array.from(ev.results).map(r => r[0].transcript).join(' ');
            setQ(txt);
        };
        r.onerror = () => setListening(false);
        r.onend = () => setListening(false);
        r.start();
        recogRef.current = r;
        setListening(true);
    };
    const stopVoice = () => {
        try { recogRef.current?.stop(); } catch { /* ignore */ }
        setListening(false);
    };

    const select = (href) => {
        setOpen(false); setQ('');
        navigate(href);
    };

    return (
        <>
            <button
                onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 80); }}
                data-testid="search-open-btn"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ring-1 ring-slate-200 bg-white hover:bg-slate-50 text-slate-500 text-sm">
                <Search className="w-4 h-4" />
                <span className="hidden sm:inline">Search</span>
                <kbd className="hidden md:inline ml-1 text-[10px] font-bold text-slate-400 bg-slate-100 rounded px-1.5 py-0.5">⌘K</kbd>
            </button>

            {open && createPortal((
                <div className="fixed inset-0 z-[70] bg-black/45 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid="search-modal">
                    <div className="max-w-xl mx-auto mt-[8vh] bg-white rounded-2xl ring-1 ring-slate-200 shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
                            <Search className="w-5 h-5 text-slate-400" />
                            <input
                                ref={inputRef}
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search courses, videos, games, stories…"
                                className="flex-1 bg-transparent outline-none text-sm text-slate-900 placeholder:text-slate-400"
                                data-testid="search-input"
                            />
                            {loading && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
                            <button
                                onClick={listening ? stopVoice : startVoice}
                                className={`p-1.5 rounded-lg ${listening ? 'bg-emerald-100 text-emerald-600 animate-pulse' : 'hover:bg-slate-100 text-slate-500'}`}
                                title={listening ? 'Listening… click to stop' : 'Voice search'}
                                data-testid="search-voice-btn">
                                <Mic className="w-4 h-4" />
                            </button>
                            <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="search-close-btn">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Suggestions */}
                        {suggestions.length > 0 && (
                            <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Suggestions</div>
                                <div className="flex flex-wrap gap-1.5">
                                    {suggestions.map(s => (
                                        <button key={s} onClick={() => setQ(s)} className="px-2.5 py-1 rounded-full bg-white ring-1 ring-slate-200 text-xs text-slate-700 hover:bg-amber-50 hover:ring-amber-300"
                                            data-testid={`search-suggest-${s.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}>
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Results */}
                        <div className="max-h-[55vh] overflow-y-auto">
                            {results.length === 0 && q && !loading && (
                                <div className="px-6 py-10 text-center text-sm text-slate-400">
                                    No matches for <span className="font-semibold text-slate-600">"{q}"</span>. Try a tag like <em>FRLS</em> or <em>safety</em>.
                                </div>
                            )}
                            {results.map(r => {
                                const Icon = KIND_ICON[r.kind] || BookOpen;
                                return (
                                    <button key={`${r.kind}-${r.id}`} onClick={() => select(r.href)}
                                        className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-amber-50 border-b border-slate-100"
                                        data-testid={`search-result-${r.kind}-${r.id}`}>
                                        <span className="w-9 h-9 grid place-items-center rounded-xl bg-slate-100 text-slate-500">
                                            <Icon className="w-4 h-4" />
                                        </span>
                                        <span className="flex-1 min-w-0">
                                            <span className="font-bold text-sm text-slate-800 block truncate">{r.title}</span>
                                            {r.description && <span className="text-xs text-slate-500 block truncate">{r.description}</span>}
                                        </span>
                                        <span className="text-[10px] uppercase font-bold text-slate-400">{r.kind}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            ), document.body)}
        </>
    );
}
